import {
  ControlledDateInput,
  ControlledInput,
} from "components/Card/ControlledInput";
import ControlledSelect from "components/inputs/Select/ControlledSelect";
import { Checkbox, MaskedInput, TextInput } from "components/inputs";
import type {
  Control,
  UseFormGetValues,
  UseFormSetValue,
} from "react-hook-form";
import type { ConstantTypes } from "types/DataTypes";
import type { LienFormValues } from "../../../LienTypes";

type ReconsiderationSectionProps = {
  control: Control<LienFormValues>;
  getValues: UseFormGetValues<LienFormValues>;
  setValue: UseFormSetValue<LienFormValues>;
  handleFieldChange: (field: string | null, value: unknown) => Promise<void>;
  constants?: ConstantTypes;
};

const ReconsiderationSection = ({
  control,
  getValues,
  setValue,
  handleFieldChange,
  constants,
}: ReconsiderationSectionProps) => {
  const hasReconsideration =
    getValues("reconsideration_appeal_by_date") !== null;

  const appealResultOptions = constants?.AppealResultChoices || [];

  return (
    <>
      <div className="header-primary flex jc-sb ai-c">
        <div className="text-sm text-gray-500">QIC Reconsideration</div>
      </div>

      <ControlledDateInput
        control={control}
        name="reconsideration_appeal_by_date"
        label="Appeal By Date"
        errors={{}}
        allowFutureDates={true}
        onSave={async (value) => {
          if (!value) {
            setValue("reconsideration_request_date", null);
            setValue("reconsideration_decision_amount", null);
            setValue("reconsideration_amount", null);
            setValue("reconsideration_amount_due", null);
            setValue("reconsideration_acknowledgement", false);
            setValue("reconsideration_appeal_no", null);
            setValue("reconsideration_decision", null);
            setValue("reconsideration_result_dollar_amount", null);
            setValue("reconsideration_savings", null);
            setValue("reconsideration_crc_letter", null);
            await handleFieldChange(null, {
              reconsideration_appeal_by_date: null,
              reconsideration_request_date: null,
              reconsideration_decision_amount: null,
              reconsideration_amount: null,
              reconsideration_amount_due: null,
              reconsideration_acknowledgement: false,
              reconsideration_appeal_no: null,
              reconsideration_decision: null,
              reconsideration_result_dollar_amount: null,
              reconsideration_savings: null,
              reconsideration_crc_letter: null,
            });
          } else {
            await handleFieldChange("reconsideration_appeal_by_date", value);
          }
        }}
      />

      {hasReconsideration && (
        <>
          <ControlledDateInput
            control={control}
            name="reconsideration_request_date"
            label="Request Date"
            errors={{}}
            allowFutureDates={true}
            onSave={(value) =>
              handleFieldChange("reconsideration_request_date", value)
            }
          />

          <ControlledInput
            control={control}
            name="reconsideration_decision_amount"
            label="Decision Amount"
            errors={{}}
            onSave={(value) =>
              handleFieldChange("reconsideration_decision_amount", value)
            }
            componentType={MaskedInput}
            mask="dollar"
          />

          <ControlledInput
            control={control}
            name="reconsideration_amount"
            label="Reconsideration Amount"
            errors={{}}
            onSave={(value) =>
              handleFieldChange("reconsideration_amount", value)
            }
            componentType={MaskedInput}
            mask="dollar"
            readOnly
          />

          <ControlledInput
            control={control}
            name="reconsideration_amount_due"
            label="Amount Due"
            errors={{}}
            onSave={(value) =>
              handleFieldChange("reconsideration_amount_due", value)
            }
            componentType={MaskedInput}
            mask="dollar"
            readOnly
          />

          <Checkbox
            name="reconsideration_acknowledgement"
            label="Acknowledgement"
            checked={getValues("reconsideration_acknowledgement")}
            required={false}
            onChange={async (e) => {
              setValue("reconsideration_acknowledgement", e.target.checked);
              await handleFieldChange(
                "reconsideration_acknowledgement",
                e.target.checked,
              );
            }}
          />

          <ControlledInput
            control={control}
            name="reconsideration_appeal_no"
            label="Appeal No."
            errors={{}}
            onSave={(value) =>
              handleFieldChange("reconsideration_appeal_no", value)
            }
            componentType={TextInput}
          />

          <ControlledSelect
            control={control}
            name="reconsideration_decision"
            label="Decision"
            errors={{}}
            options={appealResultOptions}
            onSave={(value) =>
              handleFieldChange("reconsideration_decision", value?.value)
            }
          />

          <ControlledInput
            control={control}
            name="reconsideration_result_dollar_amount"
            label="QIC Result Dollar Amount"
            errors={{}}
            onSave={(value) =>
              handleFieldChange("reconsideration_result_dollar_amount", value)
            }
            componentType={MaskedInput}
            mask="dollar"
          />

          <ControlledInput
            control={control}
            name="reconsideration_savings"
            label="Savings"
            errors={{}}
            onSave={(value) =>
              handleFieldChange("reconsideration_savings", value)
            }
            componentType={MaskedInput}
            mask="dollar"
          />

          <ControlledDateInput
            control={control}
            name="reconsideration_crc_letter"
            label="CRC Letter"
            errors={{}}
            allowFutureDates={true}
            onSave={(value) =>
              handleFieldChange("reconsideration_crc_letter", value)
            }
          />
        </>
      )}
    </>
  );
};

export default ReconsiderationSection;
